import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Radar, ShieldAlert, TrendingUp, Landmark, Pickaxe, Compass, Building2,
  Crown, Bell, Sparkles, ArrowRight, ChevronRight, Command as CommandIcon,
  MapPin, Gavel, Banknote, ClipboardCheck,
} from "lucide-react";

/**
 * Executive Attention Room — the "What requires my attention today?" landing.
 * PRESENTATION-ONLY: consumes existing endpoints (GSROS + SIC + core cadastres).
 * Does not create business logic or new data. Every panel deep-links into the
 * existing modules that already own the capability.
 */

const Section = ({ title, accent = "#C99A2E", children, right, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Kpi = ({ label, value, hint, accent = "#C99A2E", to, nav, testId }) => (
  <button
    onClick={() => to && nav(to)}
    data-testid={testId}
    className="text-left rounded-sm border px-3 py-2.5 w-full transition"
    style={{
      borderColor: accent + "30",
      background: "rgba(7,27,43,0.55)",
    }}
  >
    <div className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#7C93A5]">{label}</div>
    <div className="flex items-baseline gap-2 mt-1">
      <div className="font-display font-bold text-[#F6F8FA] text-[22px] leading-none">{value}</div>
      {hint && <div className="text-[11px]" style={{ color: accent }}>{hint}</div>}
    </div>
    {to && (
      <div className="mt-1.5 text-[10px] font-mono uppercase tracking-[0.16em] text-[#7C93A5] inline-flex items-center gap-1">
        Open <ArrowRight size={9} />
      </div>
    )}
  </button>
);

const AnswerCard = ({ question, resolvedIn, accent, children }) => (
  <div className="rounded-sm border p-3" style={{ borderColor: accent + "50", background: `linear-gradient(135deg, rgba(7,27,43,0.6), ${accent}12)` }}>
    <div className="flex items-center gap-2">
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="text-[13px] text-[#F6F8FA] font-medium">{question}</div>
      <span className="ml-auto rounded-sm px-1.5 py-0.5 text-[9.5px] font-mono uppercase tracking-[0.14em]"
             style={{ background: accent + "20", color: accent }}>
        {resolvedIn}
      </span>
    </div>
    <div className="mt-2">{children}</div>
  </div>
);

const urgencyColor = (n) => n >= 90 ? "#E67E7E" : n >= 75 ? "#C99A2E" : "#4FA3D1";

export const ExecutiveAttention = () => {
  const nav = useNavigate();
  const cabinet = useQuery({
    queryKey: ["attn-cabinet-command"],
    queryFn: async () => (await api.get("/cadastre360/gsros/cabinet-command-centre")).data,
  });
  const situation = useQuery({
    queryKey: ["attn-situation-room"],
    queryFn: async () => (await api.get("/cadastre360/sovereign/situation-room")).data,
  });
  const perf = useQuery({
    queryKey: ["attn-performance"],
    queryFn: async () => (await api.get("/cadastre360/gsros/performance-index")).data,
  });
  const risk = useQuery({
    queryKey: ["attn-risk"],
    queryFn: async () => (await api.get("/cadastre360/gsros/national-risk-register")).data,
  });

  const cab = cabinet.data;
  const sit = situation.data;
  const pf = perf.data;
  const rk = risk.data;

  const kpis = sit?.cabinet_kpis || {};
  const top = (cab?.top_priority_items || []).slice(0, 6);
  const topRisks = (rk?.risks || []).slice(0, 5);
  const provinces = (pf?.provincial_scores || []).slice(0, 3);

  return (
    <div className="p-6 space-y-4" data-testid="page-executive-attention">
      {/* Hero */}
      <div className="rounded-sm border p-6 flex items-start gap-4 relative overflow-hidden"
           style={{
             borderColor: "#7B2FBE60",
             background: "linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, rgba(123,47,190,0.28) 100%)",
           }}>
        <Radar size={38} className="text-[#C99A2E] mt-1" />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#C99A2E]">
            Cadastre360™ · Executive Attention Room
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            What requires my attention today?
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            A calm, ordered view for the executive: priorities first, operational detail last.
            Every card deep-links to the module that owns the capability.
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-[0.16em]">
            <span className="rounded-sm px-2 py-0.5 border" style={{ background: "#7B2FBE20", borderColor: "#7B2FBE60", color: "#7B2FBE" }}>
              Cabinet-grade
            </span>
            <span className="rounded-sm px-2 py-0.5 border" style={{ background: "#C99A2E20", borderColor: "#C99A2E60", color: "#C99A2E" }}>
              Strategic
            </span>
            <span className="rounded-sm px-2 py-0.5 border" style={{ background: "#6BB88A20", borderColor: "#6BB88A60", color: "#6BB88A" }}>
              Operational
            </span>
            <span className="rounded-sm px-2 py-0.5 border text-[#B3C2CC] border-white/15 inline-flex items-center gap-1">
              <CommandIcon size={9} /> K to jump
            </span>
          </div>
        </div>
      </div>

      {/* 6 answer cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <AnswerCard question="What requires my attention today?" resolvedIn="Cabinet Command Centre" accent="#7B2FBE">
          <div className="flex items-baseline gap-2">
            <div className="font-display font-bold text-[#F6F8FA] text-2xl leading-none">
              {cab?.total_items_flagged ?? "—"}
            </div>
            <div className="text-[11px] text-[#DCE3E8]">
              items · average urgency <span className="text-[#C99A2E] font-mono">{cab?.average_urgency_score ?? "—"}</span>
            </div>
          </div>
          <button onClick={() => nav("/app/gsros")}
                  data-testid="attn-goto-cabinet-command"
                  className="mt-2 rounded-sm px-2.5 py-1 text-[10.5px] font-mono uppercase tracking-[0.14em] border border-white/10 text-[#F6F8FA] inline-flex items-center gap-1">
            Open Cabinet Command Centre <ChevronRight size={11} />
          </button>
        </AnswerCard>

        <AnswerCard question="What decisions await approval?" resolvedIn="Applications" accent="#6BB88A">
          <div className="flex items-baseline gap-2">
            <div className="font-display font-bold text-[#F6F8FA] text-2xl leading-none">
              {kpis.applications_open ?? "—"}
            </div>
            <div className="text-[11px] text-[#DCE3E8]">applications open · {kpis.inspections_due ?? 0} inspections due</div>
          </div>
          <button onClick={() => nav("/app/applications")}
                  data-testid="attn-goto-applications"
                  className="mt-2 rounded-sm px-2.5 py-1 text-[10.5px] font-mono uppercase tracking-[0.14em] border border-white/10 text-[#F6F8FA] inline-flex items-center gap-1">
            Open Applications <ChevronRight size={11} />
          </button>
        </AnswerCard>

        <AnswerCard question="Where is national risk increasing?" resolvedIn="National Risk Register" accent="#E67E7E">
          <div className="flex items-baseline gap-2">
            <div className="font-display font-bold text-[#F6F8FA] text-2xl leading-none">
              {rk?.composite_score ?? "—"}
            </div>
            <div className="text-[11px] text-[#DCE3E8]">composite score · {rk?.risk_count ?? 0} risks tracked</div>
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {topRisks.slice(0, 4).map((r) => (
              <span key={r.id} className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono"
                    style={{ background: "#E67E7E20", color: "#E67E7E" }}>
                {r.risk}
              </span>
            ))}
          </div>
        </AnswerCard>

        <AnswerCard question="How is the sovereign performance trending?" resolvedIn="National Performance Index" accent="#C99A2E">
          <div className="flex items-baseline gap-2">
            <div className="font-display font-bold text-[#F6F8FA] text-2xl leading-none">
              {pf?.national_score ?? "—"}
            </div>
            <div className="text-[11px] text-[#DCE3E8]">grade <span className="text-[#C99A2E] font-mono">{pf?.grade ?? "—"}</span></div>
          </div>
          <button onClick={() => nav("/app/gsros")}
                  data-testid="attn-goto-performance-index"
                  className="mt-2 rounded-sm px-2.5 py-1 text-[10.5px] font-mono uppercase tracking-[0.14em] border border-white/10 text-[#F6F8FA] inline-flex items-center gap-1">
            Open Performance Index <ChevronRight size={11} />
          </button>
        </AnswerCard>

        <AnswerCard question="Where is revenue changing?" resolvedIn="Revenue Ledger" accent="#4FA3D1">
          <div className="text-[11px] text-[#DCE3E8]">Collected</div>
          <div className="font-display font-bold text-[#F6F8FA] text-lg leading-none">
            USD {Number(kpis.revenue_collected_usd || 0).toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-[#DCE3E8]">
            Outstanding <span className="font-mono text-[#E67E7E]">
              USD {Number(kpis.revenue_outstanding_usd || 0).toLocaleString()}
            </span>
          </div>
          <button onClick={() => nav("/app/revenue")}
                  data-testid="attn-goto-revenue"
                  className="mt-2 rounded-sm px-2.5 py-1 text-[10.5px] font-mono uppercase tracking-[0.14em] border border-white/10 text-[#F6F8FA] inline-flex items-center gap-1">
            Open Revenue Ledger <ChevronRight size={11} />
          </button>
        </AnswerCard>

        <AnswerCard question="Which provinces require intervention?" resolvedIn="Provincial Executive Dashboards" accent="#7B2FBE">
          <div className="space-y-1">
            {provinces.map((p) => (
              <div key={p.province} className="flex items-center gap-2 text-[11.5px]">
                <div className="w-6 font-mono text-[#7C93A5]">#{p.rank}</div>
                <div className="flex-1 text-[#F6F8FA]">{p.province}</div>
                <div className="font-mono text-[#F6F8FA]">{p.score}</div>
                <div className={"w-12 text-right font-mono text-[10px] " + (p.trend_yoy_pct >= 0 ? "text-[#6BB88A]" : "text-[#E67E7E]")}>
                  {p.trend_yoy_pct >= 0 ? "+" : ""}{p.trend_yoy_pct}%
                </div>
              </div>
            ))}
          </div>
        </AnswerCard>
      </div>

      {/* Priority queue */}
      <Section title="Priority queue — urgency-scored" accent="#7B2FBE" testId="attn-priority-queue">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {top.map((it) => (
            <div key={it.id} className="flex items-start gap-3 border border-white/5 rounded-sm p-2.5"
                 style={{ background: "rgba(7,27,43,0.45)" }}>
              <div className="rounded-sm px-2 py-0.5 font-mono text-[11px]"
                   style={{ background: urgencyColor(it.urgency_score) + "30", color: urgencyColor(it.urgency_score) }}>
                {it.urgency_score}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] text-[#F6F8FA] font-medium">{it.detail}</div>
                <div className="text-[10px] font-mono text-[#7C93A5]">{it.portfolio}</div>
                <div className="text-[11px] text-[#DCE3E8] mt-0.5">→ {it.recommended_action}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* KPI strip */}
      <Section title="Strategic KPIs" accent="#C99A2E" testId="attn-strategic-kpis">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <Kpi label="Parcels registered"   value={Number(kpis.parcels_total || 0).toLocaleString()}    accent="#C99A2E" to="/app/land"        nav={nav} testId="attn-kpi-parcels" />
          <Kpi label="Titled parcels"       value={Number(kpis.parcels_titled || 0).toLocaleString()}  accent="#C99A2E" to="/app/registry"    nav={nav} testId="attn-kpi-titled" />
          <Kpi label="Mineral rights active"value={Number(kpis.mining_active || 0).toLocaleString()}    accent="#C99A2E" to="/app/mining"      nav={nav} testId="attn-kpi-mining" />
          <Kpi label="Survey records"       value={Number(kpis.survey_records || 0).toLocaleString()}  accent="#C99A2E" to="/app/survey"      nav={nav} testId="attn-kpi-surveys" />
          <Kpi label="Open disputes"        value={Number(kpis.disputes_open || 0).toLocaleString()}   accent="#E67E7E" to="/app/disputes"    nav={nav} testId="attn-kpi-disputes" />
          <Kpi label="Mining conflicts"     value={Number(kpis.mining_conflicts || 0).toLocaleString()}accent="#E67E7E" to="/app/mining"      nav={nav} testId="attn-kpi-conflicts" />
        </div>
      </Section>

      {/* Executive shortcuts */}
      <Section title="Executive shortcuts" accent="#4FA3D1" testId="attn-shortcuts">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { to: "/app/sovereign-intelligence", label: "Sovereign Intelligence", icon: Crown, accent: "#C99A2E" },
            { to: "/app/gsros",                  label: "GSROS Orchestration",    icon: Building2, accent: "#7B2FBE" },
            { to: "/app/briefings",              label: "Executive Briefings",    icon: Sparkles, accent: "#C99A2E" },
            { to: "/app/twin",                   label: "National Digital Twin",  icon: Radar, accent: "#4FA3D1" },
            { to: "/app/notifications",          label: "Notification Centre",    icon: Bell, accent: "#6BB88A" },
            { to: "/app/national-deployment",    label: "National Deployment",    icon: MapPin, accent: "#4FA3D1" },
            { to: "/app/mining-migration/command", label: "Migration Command",    icon: Compass, accent: "#6BB88A" },
            { to: "/app/reports",                label: "Reports & Exports",      icon: TrendingUp, accent: "#4FA3D1" },
          ].map((s) => (
            <button key={s.to} onClick={() => nav(s.to)}
                    data-testid={"attn-shortcut-" + s.to.replace(/\//g, "_")}
                    className="text-left rounded-sm border px-3 py-2 inline-flex items-center gap-2 transition"
                    style={{ borderColor: s.accent + "30", background: "rgba(7,27,43,0.55)" }}>
              <s.icon size={14} style={{ color: s.accent }} />
              <div className="text-[12px] text-[#F6F8FA] flex-1">{s.label}</div>
              <ChevronRight size={12} className="text-[#7C93A5]" />
            </button>
          ))}
        </div>
      </Section>

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] flex items-center gap-3 pt-1">
        <span>Cadastre360™ · Executive Experience v1.9</span>
        <span>·</span>
        <span>Consumes existing enterprise services — no new business logic</span>
      </div>
    </div>
  );
};

export default ExecutiveAttention;
